import { useState, type JSX } from 'react';
import { useIntl } from 'react-intl';
import { useIsEvidenceSubagentResidualCapabilitiesEnabled } from '../../flags';
import type { VisualDxDiagnosticFlowSelections } from '../interactive-widget/submission';
import { EvidenceToolFallbackPill } from '../timeline-blocks/EvidenceToolFallbackPill';
import { visualDxAgentToolMessages as messages } from './VisualDxAgentToolBlock.messages';
import {
  httpsUrl,
  isInProgress,
  readCompare,
  readDetail,
  readDiagnoses,
  readDifferential,
  readDrugReaction,
  readGallery,
  readSelections,
  toStaticStatus,
} from './VisualDxAgentToolBlock.readers';
import { VisualDxBlock } from './VisualDxBlock';
import { VisualDxCompare } from './VisualDxCompare';
import { VisualDxDiagnosticFlow } from './VisualDxDiagnosticFlow';
import { VisualDxDrugReaction } from './VisualDxDrugReaction';
import { VisualDxGallery } from './VisualDxGallery';
import { VisualDxRankedDifferential } from './VisualDxRankedDifferential';

interface VisualDxAgentToolBlockProps {
  name: string;
  input: Record<string, unknown>;
  status: string;
  data?: Record<string, unknown> | null;
  error?: string;
  approvalId?: string;
  onResolve?: (
    approvalId: string,
    selections: VisualDxDiagnosticFlowSelections
  ) => Promise<void>;
}

const DETAIL_TOOL = 'get_diagnostic_info';
const ENRICH_TOOL = 'enrich_diagnoses_with_links';
const START_TOOL = 'start_visualdx_diagnostic_flow';
const RANKED_TOOL = 'render_visualdx_ranked_differential';
const GALLERY_TOOL = 'get_diagnosis_gallery';
const COMPARE_TOOL = 'compare_ddx_view';
const DRUG_TOOL = 'get_drug_reaction_diagnoses';
const VISUALDX_AGENT_TOOL_NAMES = new Set([
  DETAIL_TOOL,
  ENRICH_TOOL,
  START_TOOL,
  RANKED_TOOL,
  GALLERY_TOOL,
  COMPARE_TOOL,
  DRUG_TOOL,
]);

export const isVisualDxAgentToolName = (name: string): boolean =>
  VISUALDX_AGENT_TOOL_NAMES.has(name);

export const VisualDxAgentToolBlock = ({
  name,
  input,
  status,
  data,
  error,
  approvalId,
  onResolve,
}: VisualDxAgentToolBlockProps): JSX.Element | null => {
  const residualCapabilitiesEnabled = useIsEvidenceSubagentResidualCapabilitiesEnabled();
  const [pendingSelections, setPendingSelections] =
    useState<VisualDxDiagnosticFlowSelections>();
  const intl = useIntl();
  if (!residualCapabilitiesEnabled) return null;

  if (name === DETAIL_TOOL) {
    return (
      <VisualDxBlock
        tool={DETAIL_TOOL}
        status={toStaticStatus(status)}
        query={
          typeof input.diagnosis_query === 'string' ? input.diagnosis_query : undefined
        }
        error={error}
        detail={readDetail(data)}
      />
    );
  }

  if (name === ENRICH_TOOL) {
    const diagnosisNames = Array.isArray(input.diagnosis_names)
      ? input.diagnosis_names.filter(
          (diagnosisName): diagnosisName is string => typeof diagnosisName === 'string'
        )
      : [];
    return (
      <VisualDxBlock
        tool={ENRICH_TOOL}
        status={toStaticStatus(status)}
        query={diagnosisNames.join(', ') || undefined}
        error={error}
        diagnoses={readDiagnoses(data)}
        compare_url={httpsUrl(data?.compare_url)}
      />
    );
  }

  if (name === START_TOOL) {
    const submittedSelections = readSelections(data) ?? pendingSelections;
    if (!approvalId && !submittedSelections) {
      return (
        <EvidenceToolFallbackPill
          name={name}
          label={intl.formatMessage(messages.diagnosticFlowStatus)}
          active={isInProgress(status)}
        />
      );
    }
    return (
      <VisualDxDiagnosticFlow
        submittedSelections={submittedSelections}
        onSubmit={
          approvalId && onResolve
            ? async (selections) => {
                setPendingSelections(selections);
                try {
                  await onResolve(approvalId, selections);
                } catch (error) {
                  setPendingSelections(undefined);
                  throw error;
                }
              }
            : undefined
        }
      />
    );
  }

  if (name === RANKED_TOOL) {
    const differential = readDifferential(data);
    return status === 'complete' && differential.length > 0 ? (
      <VisualDxRankedDifferential differential={differential} />
    ) : (
      <EvidenceToolFallbackPill
        name={name}
        label={intl.formatMessage(messages.rankedDifferentialStatus)}
        active={isInProgress(status)}
      />
    );
  }

  if (name === GALLERY_TOOL) {
    const gallery = readGallery(data);
    return status === 'complete' && gallery != null ? (
      <VisualDxGallery gallery={gallery} />
    ) : (
      <EvidenceToolFallbackPill
        name={name}
        label={intl.formatMessage(messages.galleryStatus)}
        active={isInProgress(status)}
      />
    );
  }

  if (name === COMPARE_TOOL) {
    const compare = readCompare(data);
    return status === 'complete' && compare != null ? (
      <VisualDxCompare compare={compare} />
    ) : (
      <EvidenceToolFallbackPill
        name={name}
        label={intl.formatMessage(messages.compareStatus)}
        active={isInProgress(status)}
      />
    );
  }

  if (name === DRUG_TOOL) {
    const drugReaction = readDrugReaction(data);
    return status === 'complete' && drugReaction != null ? (
      <VisualDxDrugReaction drugReaction={drugReaction} />
    ) : (
      <EvidenceToolFallbackPill
        name={name}
        label={intl.formatMessage(messages.drugReactionStatus)}
        active={isInProgress(status)}
      />
    );
  }

  return null;
};
